import { intl } from '@approvalmax/utils';
import clamp from 'lodash/clamp';
import { DiscountType } from 'modules/data/backend';
import {
    changeXeroLineItemDiscount,
    changeXeroLineItemDiscountAmount,
    changeXeroLineItemDiscountType,
} from 'pages/requestForm/actions';
import { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { regex } from './XeroDiscountCell.constants';
import { messages } from './XeroDiscountCell.messages';
import { TextEditorStyled } from './XeroDiscountCell.styles';
import { XeroDiscountCellProps } from './XeroDiscountCell.types';
import { toDiscountStateValue } from './XeroDiscountCell.utils';

const XeroDiscountCell = memo(
    forwardRef<HTMLInputElement, XeroDiscountCellProps>((props, ref) => {
        const { invalid, value: discountValue, discountType, li } = props;

        const [discountStateValue, setDiscountStateValue] = useState(toDiscountStateValue(discountValue, discountType));
        const [focused, setFocused] = useState(false);
        const dispatch = useDispatch();

        const onFocus = useCallback(() => {
            setFocused(true);
        }, []);

        const onBlur = useCallback(() => {
            setFocused(false);
            setDiscountStateValue(toDiscountStateValue(discountValue, discountType));
        }, [discountType, discountValue]);

        const inputValue = useMemo(() => {
            if (focused) {
                return discountStateValue;
            }

            switch (discountType) {
                case DiscountType.Rate:
                case DiscountType.PreRate:
                    return discountValue ? `${intl.formatNumber(discountValue, 2)}%` : '';

                case DiscountType.Amount:
                case DiscountType.PreAmount:
                    return discountValue ? intl.formatNumber(discountValue, 2) : '';
            }
        }, [focused, discountType, discountStateValue, discountValue]);

        const renewDiscount = useCallback(
            (newDiscount: number | null, newDiscountType: DiscountType) => {
                if (newDiscountType !== discountType) {
                    dispatch(changeXeroLineItemDiscountType(li.id, newDiscountType));
                }

                if (newDiscountType === DiscountType.Rate) {
                    dispatch(changeXeroLineItemDiscount(li.id, newDiscount));
                    dispatch(changeXeroLineItemDiscountAmount(li.id, null));
                } else {
                    dispatch(changeXeroLineItemDiscount(li.id, null));
                    dispatch(changeXeroLineItemDiscountAmount(li.id, newDiscount));
                }
            },
            [discountType, dispatch, li.id]
        );

        const handleChangeInputValue = useCallback(
            (newValue: string) => {
                if (regex.test(newValue) || newValue === '') {
                    setDiscountStateValue(newValue);

                    if (newValue.slice(-1) === '%') {
                        const newDiscount = newValue !== '' ? clamp(Number(newValue.slice(0, -1)), 0, 100) : null;

                        renewDiscount(newDiscount || null, DiscountType.Rate);
                    } else {
                        const amount = li.unitPrice && li.qty ? li.unitPrice * li.qty : 0;
                        const newDiscount = newValue !== '' ? clamp(Number(newValue), 0, amount) : null;

                        renewDiscount(newDiscount || null, DiscountType.Amount);
                    }
                }
            },
            [li.qty, li.unitPrice, renewDiscount]
        );

        return (
            <TextEditorStyled
                ref={ref}
                invalid={invalid}
                value={inputValue}
                onChange={handleChangeInputValue}
                placeholder={messages.cellPlaceholderDiscount}
                theme='transparent'
                changeOnBlur={false}
                onFocus={onFocus}
                onBlur={onBlur}
            />
        );
    })
);

export default XeroDiscountCell;
